import { ActionIcon, Box, Text } from "@mantine/core"
import { TrashIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import type { PriceBreakdown } from "../../../apis/pricing"
import type { CartItem } from "../../../store/Cart"
import { currencyFormat } from "../../../utils/currency"
import {
  breakdownKey,
  extraDiscountOf,
  networkCanOrder,
  supplierCode,
  type CartPreviews,
} from "./cart-model"
import classes from "./CartDrawer.module.css"
import { GiftLines, GroupPromoSummary, ThresholdProgressList } from "./CartPromotions"
import { describePrice } from "./price-labels"
import { ProductThumb } from "./ProductThumb"
import { QtyStepper } from "./QtyStepper"
import type { SupplierCartGroup } from "./types"
import { cleanName, getMinOrderQuantity } from "./utils"

type Props = {
  cartBySupplier: Record<string, SupplierCartGroup>
  networks: Record<string, any>
  breakdowns: Record<string, PriceBreakdown>
  /** POST /ord/cart/preview per supplier: gift lines, bundle / threshold discount, threshold progress. */
  previews: CartPreviews
  onUpdateQuantity: (productId: string, supplierBusinessId: string, qty: number) => void
  onRemoveFromCart: (productId: string, supplierBusinessId: string) => void
}

/**
 * Cart lines grouped by supplier (canvas OrdB_Cart, OrdB_PromoGift, OrdB_PromoTier):
 * grey supplier header, threshold progress, one row per line, then the gift
 * rows the order service will add and the group's paid / gift subtotal.
 */
export function CartDrawerItems({ cartBySupplier, networks, breakdowns, previews, onUpdateQuantity, onRemoveFromCart }: Props) {
  const { t } = useTranslation("order")

  return (
    <>
      {Object.entries(cartBySupplier).map(([supplierId, group]) => {
        const network = networks[supplierId]
        const code = supplierCode(network)
        const preview = previews[supplierId]
        const discount = extraDiscountOf(previews, supplierId, group.totalAmount)
        return (
          <Box key={supplierId} component="section" className={`${classes.card} ${classes.group}`} aria-label={group.supplierName}>
            <Box className={classes.groupHead}>
              <Box miw={0}>
                <Text className={classes.groupName}>
                  {group.supplierName}
                  {code && (
                    <Text span className={`${classes.sub} ${classes.mono}`} ml="xs">
                      #{code}
                    </Text>
                  )}
                </Text>
                {!networkCanOrder(network) && (
                  <Text role="alert" className={classes.sub} c="red.8">
                    {t("buyerCustomer.cannotOrderInactive")}
                  </Text>
                )}
              </Box>
              <Box className={classes.groupTotal}>
                <Text className={classes.sub}>{t("cart.groupTotal")}</Text>
                <Text className={classes.amount}>{currencyFormat(group.totalAmount - discount)}</Text>
              </Box>
            </Box>

            <ThresholdProgressList preview={preview} />

            {group.items.map((item) => (
              <CartLine
                key={`${item.supplierBusinessId}-${item.id}`}
                item={item}
                breakdown={breakdowns[breakdownKey(item.supplierBusinessId, item.id)]}
                onUpdateQuantity={onUpdateQuantity}
                onRemoveFromCart={onRemoveFromCart}
              />
            ))}

            <GiftLines preview={preview} items={group.items} />
            <GroupPromoSummary amount={group.totalAmount} preview={preview} discount={discount} />
          </Box>
        )
      })}
    </>
  )
}

type LineProps = {
  item: CartItem
  breakdown?: PriceBreakdown
  onUpdateQuantity: Props["onUpdateQuantity"]
  onRemoveFromCart: Props["onRemoveFromCart"]
}

function CartLine({ item, breakdown, onUpdateQuantity, onRemoveFromCart }: LineProps) {
  const { t } = useTranslation("order")
  const name = cleanName(item.nameMon) || item.skuCode || "-"
  const minOrder = getMinOrderQuantity(item)
  const view = describePrice(t, breakdown, item.price)
  const source = item.isConsignment ? t("cart.consignmentPrice") : view.source

  return (
    <Box className={classes.line}>
      <Box className={classes.thumb}>
        <ProductThumb src={item.image} alt={name} iconSize={20} />
      </Box>
      <Box miw={0}>
        <Text className={classes.lineName}>{name}</Text>
        {item.skuCode && <Text className={`${classes.sub} ${classes.mono}`}>{item.skuCode}</Text>}
        <Box className={classes.priceRow}>
          <Text span className={classes.amount}>
            {currencyFormat(item.price)}
          </Text>
          <Text span className={classes.sub}>
            {source}
          </Text>
        </Box>
        <Box className={classes.qtyRow}>
          <Text span className={classes.sub}>
            {t("cart.qty")}
          </Text>
          <Box className={classes.qtyStepper}>
            <QtyStepper
              value={item.quantity}
              min={minOrder}
              label={t("cart.qtyLabel", { name })}
              onChange={(qty) => onUpdateQuantity(item.id, item.supplierBusinessId, qty)}
            />
          </Box>
          <Text span className={classes.sub}>
            {t("cart.minQty", { count: minOrder })}
          </Text>
        </Box>
      </Box>
      <Box className={classes.lineSide}>
        <Text className={classes.amount}>{currencyFormat(item.price * item.quantity)}</Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={36}
          aria-label={t("cart.remove", { name })}
          onClick={() => onRemoveFromCart(item.id, item.supplierBusinessId)}
        >
          <TrashIcon size={18} />
        </ActionIcon>
      </Box>
    </Box>
  )
}
